import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { securityService } from '../services/securityService';
import { Guest } from '../types/security';
import { buildSearchParams } from '../utils/guestSearchFilter';
import {
  getProvinces,
  applyProvinceChange,
  applyCityChange,
  applyHotelChange,
  GeoFilterState,
  initialGeoFilterState,
} from '../utils/geoFilterUtils';

function GuestsListPage() {
  const [guests, setGuests] = useState<Guest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);

  // Search fields
  const [searchName, setSearchName] = useState('');
  const [searchPassport, setSearchPassport] = useState('');
  const [searchPhone, setSearchPhone] = useState('');
  const [searchDob, setSearchDob] = useState('');
  const [filterRisk, setFilterRisk] = useState<string>('all');

  // Geo filter state
  const [geoFilter, setGeoFilter] = useState<GeoFilterState>(initialGeoFilterState());
  const provinces = getProvinces();

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchGuests = useCallback(async (
    name: string,
    passport: string,
    phone: string,
    dateOfBirth: string,
    province?: string,
    city?: string,
    hotelId?: string
  ) => {
    setLoading(true);
    setError(null);
    try {
      const params = buildSearchParams({ name, passport, phone, dateOfBirth, province, city, hotelId });
      const response = await securityService.getLiveGuests(params);
      if (response && response.data) {
        setGuests(response.data);
        setTotal(response.total || response.data.length);
      } else if (Array.isArray(response)) {
        // Fallback if API returns a plain array
        const arr = response as unknown as Guest[];
        setGuests(arr);
        setTotal(arr.length);
      } else {
        setGuests([]);
        setTotal(0);
      }
    } catch (err: any) {
      console.error('Error fetching guests:', err);
      if (err.response?.status === 401) {
        setError('Session expirée. Veuillez vous reconnecter.');
      } else {
        setError('Erreur de connexion au serveur. Veuillez réessayer.');
      }
      setGuests([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load — only if authenticated
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      setError('Veuillez vous connecter pour accéder aux dossiers invités.');
      return;
    }
    fetchGuests('', '', '', '', '', '', '');
  }, [fetchGuests]);

  // Debounced search - triggers 300ms after last change
  const debouncedSearch = useCallback((
    name: string,
    passport: string,
    phone: string,
    dob: string,
    province?: string,
    city?: string,
    hotelId?: string
  ) => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      fetchGuests(name, passport, phone, dob, province, city, hotelId);
    }, 300);
  }, [fetchGuests]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  const handleNameChange = (value: string) => {
    setSearchName(value);
    debouncedSearch(value, searchPassport, searchPhone, searchDob, geoFilter.province, geoFilter.city, geoFilter.hotelId);
  };

  const handlePassportChange = (value: string) => {
    setSearchPassport(value);
    debouncedSearch(searchName, value, searchPhone, searchDob, geoFilter.province, geoFilter.city, geoFilter.hotelId);
  };

  const handlePhoneChange = (value: string) => {
    setSearchPhone(value);
    debouncedSearch(searchName, searchPassport, value, searchDob, geoFilter.province, geoFilter.city, geoFilter.hotelId);
  };

  const handleDobChange = (value: string) => {
    setSearchDob(value);
    debouncedSearch(searchName, searchPassport, searchPhone, value, geoFilter.province, geoFilter.city, geoFilter.hotelId);
  };

  // Geo filter handlers
  const handleProvinceChange = (province: string) => {
    const newState = applyProvinceChange(province);
    setGeoFilter(newState);
    fetchGuests(searchName, searchPassport, searchPhone, searchDob, newState.province, newState.city, newState.hotelId);
  };

  const handleCityChange = async (city: string) => {
    const newState = applyCityChange(geoFilter, city);
    // Try to load hotels for this city
    if (city) {
      try {
        const stats = await securityService.getGlobalStats(geoFilter.province);
        // If the API returns hotel data, use it; otherwise keep empty
        if (stats && stats.totalHotels > 0) {
          // Hotels will be fetched from live-guests response or a dedicated endpoint
          // For now, we set available hotels from any available source
        }
      } catch {
        // Ignore - hotels list stays empty
      }
    }
    setGeoFilter(newState);
    fetchGuests(searchName, searchPassport, searchPhone, searchDob, geoFilter.province, newState.city, newState.hotelId);
  };

  const handleHotelChange = (hotelId: string) => {
    const newState = applyHotelChange(geoFilter, hotelId);
    setGeoFilter(newState);
    fetchGuests(searchName, searchPassport, searchPhone, searchDob, geoFilter.province, geoFilter.city, newState.hotelId);
  };

  const getRiskColor = (riskLevel: string) => {
    switch (riskLevel?.toLowerCase()) {
      case 'high':
      case 'élevé':
      case 'critical':
        return 'text-red-600 bg-red-100';
      case 'medium':
      case 'moyen':
        return 'text-yellow-600 bg-yellow-100';
      case 'low':
      case 'faible':
      case 'none':
        return 'text-green-600 bg-green-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getRiskLabel = (guest: Guest): string => {
    if (guest.security_score?.risk_level) return guest.security_score.risk_level;
    return '';
  };

  // Client-side risk filter (risk level comes from data, not a search param)
  const filteredGuests = filterRisk === 'all'
    ? guests
    : guests.filter(g => {
        const level = getRiskLabel(g).toLowerCase();
        return level === filterRisk.toLowerCase();
      });

  const handleRetry = () => {
    fetchGuests(searchName, searchPassport, searchPhone, searchDob, geoFilter.province, geoFilter.city, geoFilter.hotelId);
  };

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div className="px-4 py-6 sm:px-0">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">👤 Dossiers Invités</h1>
          <p className="text-gray-600">Consultation des fiches invités et scores de sécurité</p>
        </div>

        {/* Search Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label htmlFor="search-name" className="block text-sm font-medium text-gray-700 mb-2">
                Nom / Passeport / Hôtel
              </label>
              <input
                id="search-name"
                type="text"
                value={searchName}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="Nom, passeport, hôtel..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label htmlFor="search-phone" className="block text-sm font-medium text-gray-700 mb-2">
                Téléphone
              </label>
              <input
                id="search-phone"
                type="tel"
                value={searchPhone}
                onChange={(e) => handlePhoneChange(e.target.value)}
                placeholder="+243..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label htmlFor="search-dob" className="block text-sm font-medium text-gray-700 mb-2">
                Date de naissance
              </label>
              <input
                id="search-dob"
                type="date"
                value={searchDob}
                onChange={(e) => handleDobChange(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label htmlFor="search-passport" className="block text-sm font-medium text-gray-700 mb-2">
                Passeport / Document
              </label>
              <input
                id="search-passport"
                type="text"
                value={searchPassport}
                onChange={(e) => handlePassportChange(e.target.value)}
                placeholder="Numéro de document..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label htmlFor="filter-risk" className="block text-sm font-medium text-gray-700 mb-2">
                Niveau de risque
              </label>
              <select
                id="filter-risk"
                value={filterRisk}
                onChange={(e) => setFilterRisk(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">Tous</option>
                <option value="high">Élevé</option>
                <option value="medium">Moyen</option>
                <option value="low">Faible</option>
              </select>
            </div>
          </div>
        </div>

        {/* Geographic Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">🌍 Filtres géographiques</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="filter-province" className="block text-sm font-medium text-gray-700 mb-2">
                Province
              </label>
              <select
                id="filter-province"
                value={geoFilter.province}
                onChange={(e) => handleProvinceChange(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Toutes les provinces</option>
                {provinces.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="filter-city" className="block text-sm font-medium text-gray-700 mb-2">
                Ville
              </label>
              <select
                id="filter-city"
                value={geoFilter.city}
                disabled={!geoFilter.province}
                onChange={(e) => handleCityChange(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">Toutes les villes</option>
                {geoFilter.availableCities.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="filter-hotel" className="block text-sm font-medium text-gray-700 mb-2">
                Hôtel
              </label>
              <select
                id="filter-hotel"
                value={geoFilter.hotelId}
                disabled={!geoFilter.city}
                onChange={(e) => handleHotelChange(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">Tous les hôtels</option>
                {geoFilter.availableHotels.map((h) => (
                  <option key={h.id} value={h.id}>{h.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center justify-between">
            <span className="text-red-700">{error}</span>
            <button
              onClick={handleRetry}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Réessayer
            </button>
          </div>
        )}

        {/* Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Total Invités</div>
            <div className="text-2xl font-bold text-gray-900">{total}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Risque Élevé</div>
            <div className="text-2xl font-bold text-red-600">
              {guests.filter(g => {
                const l = getRiskLabel(g).toLowerCase();
                return l === 'high' || l === 'élevé' || l === 'critical';
              }).length}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Risque Moyen</div>
            <div className="text-2xl font-bold text-yellow-600">
              {guests.filter(g => {
                const l = getRiskLabel(g).toLowerCase();
                return l === 'medium' || l === 'moyen';
              }).length}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Risque Faible</div>
            <div className="text-2xl font-bold text-green-600">
              {guests.filter(g => {
                const l = getRiskLabel(g).toLowerCase();
                return l === 'low' || l === 'faible' || l === 'none';
              }).length}
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="text-xl text-gray-500">Chargement...</div>
          </div>
        )}

        {/* Guests Table */}
        {!loading && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invité
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Document
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Hôtel
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Séjour
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Score
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Risque
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredGuests.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                      {geoFilter.province
                        ? 'Aucun invité trouvé pour cette zone géographique'
                        : 'Aucun invité trouvé'}
                    </td>
                  </tr>
                ) : (
                  filteredGuests.map((guest) => (
                    <tr key={guest.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {guest.first_name} {guest.last_name}
                            </div>
                            <div className="text-sm text-gray-500">{guest.nationality}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {guest.document_number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {guest.hotel_name || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {guest.checkin_date && (
                          <>
                            <div>{new Date(guest.checkin_date).toLocaleDateString()}</div>
                            {guest.checkout_date && (
                              <div className="text-xs">→ {new Date(guest.checkout_date).toLocaleDateString()}</div>
                            )}
                          </>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900">
                          {guest.security_score ? `${guest.security_score.score}/4` : '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getRiskLabel(guest) && (
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getRiskColor(getRiskLabel(guest))}`}>
                            {getRiskLabel(guest)}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <Link
                          to={`/guests/${guest.id}`}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Voir détails
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default GuestsListPage;
